import React, { useState } from 'react';
import dayjs from 'dayjs';
import { FiEdit2, FiTrash2, FiPlus, FiInfo } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
// import { StatusBadge } from '@components/StatusBadge'; // only used by the commented-out Type column below
import { DatePill } from '@components/DatePill';
import { Table } from '@components/Table';
import { ConfirmActionDialog } from '@components/ConfirmActionDialog';
import { EmptyState } from '@components/EmptyState';
import { TooltipWrapper } from '@components/TooltipWrapper';

import {
  // HOLIDAY_TYPE_META, // only used by the commented-out Type column below
  // getHolidayType, // only used by the commented-out Type column below
  getDatePillColor,
} from './HolidayListEditor.constants';

import styles from './HolidayListEditor.module.scss';

const sortByDate = (holidays, reverse) => {
  const sorted = [...holidays].sort((a, b) => a.date.localeCompare(b.date));
  return reverse ? sorted.reverse() : sorted;
};

export const HolidayListEditor = ({
  holidays,
  onEdit,
  onRemove,
  onAdd,
  disabled,
}) => {
  const [reverseSort, setReverseSort] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const sortedHolidays = sortByDate(holidays, reverseSort);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const succeeded = await onRemove(deleteTarget.date);
    setIsDeleting(false);
    if (succeeded) setDeleteTarget(null);
  };

  const columns = [
    {
      id: 'date',
      name: 'Date',
      sortable: true,
      sortKey: 'date',
      cellRenderer: (row, rowIndex) => (
        <DatePill
          day={dayjs(row.date).format('DD')}
          month={dayjs(row.date).format('MMM').toUpperCase()}
          color={getDatePillColor(rowIndex)}
        />
      ),
    },
    {
      id: 'day',
      name: 'Day',
      cellRenderer: (row) => (
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color="gray-700"
        >
          {dayjs(row.date).format('dddd')}
        </Text>
      ),
    },
    {
      id: 'name',
      name: 'Holiday Name',
      cellRenderer: (row) => (
        <div className={styles.nameCell}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Semibold}
            color="gray-900"
          >
            {row.name}
          </Text>
          {row.description && (
            <TooltipWrapper tooltip={row.description}>
              <FiInfo size={13} color="var(--gray-400)" />
            </TooltipWrapper>
          )}
        </div>
      ),
    },
    // Type (Public/Optional) is not fully wired - optional holidays aren't
    // actually enforced or selectable anywhere for employees, so the column
    // is hidden.
    // {
    //   id: 'type',
    //   name: 'Type',
    //   cellRenderer: (row) => {
    //     const type = getHolidayType(row);
    //     return (
    //       <StatusBadge
    //         dot
    //         label={HOLIDAY_TYPE_META[type].label}
    //         tone={HOLIDAY_TYPE_META[type].tone}
    //       />
    //     );
    //   },
    // },
    {
      id: 'actions',
      name: 'Actions',
      cellRenderer: (row) => (
        <div className={styles.rowActions}>
          <Button
            variant={buttonVariants.ghost}
            icon={<FiEdit2 size={15} />}
            onClick={() => onEdit(row)}
            disabled={disabled}
            ariaLabel={`Edit ${row.name}`}
          />
          <Button
            variant={buttonVariants.ghost_danger}
            icon={<FiTrash2 size={15} />}
            onClick={() => setDeleteTarget(row)}
            disabled={disabled}
            ariaLabel={`Remove ${row.name}`}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {sortedHolidays.length === 0 ? (
        <EmptyState
          illustration
          title="No Holidays Added Yet"
          description="Add your company's public and festival holidays so they're reflected in employee calendars and attendance."
          action={
            <Button
              text="Add Holiday"
              variant={buttonVariants.primary}
              icon={<FiPlus size={14} />}
              onClick={onAdd}
            />
          }
        />
      ) : (
        <Table
          className={styles.holidayTable}
          data={sortedHolidays}
          columns={columns}
          sortedBy="date"
          reverseSort={reverseSort}
          onSortClick={(_id, reverse) => setReverseSort(reverse)}
        />
      )}

      <ConfirmActionDialog
        visible={!!deleteTarget}
        onHide={() => !isDeleting && setDeleteTarget(null)}
        title="Delete holiday"
        message={`Delete "${deleteTarget?.name}"? This can't be undone.`}
        confirmLabel="Delete holiday"
        onConfirm={confirmDelete}
        loading={isDeleting}
      />
    </>
  );
};

HolidayListEditor.displayName = 'HolidayListEditor';
